/**
 * Everything the hardware-readiness screen says, and every number it decides with (CAM-01).
 *
 * Copy and thresholds live in the config layer rather than in the screen, for the same reason
 * gameplay timings do (D-010): a playtest that changes what a five-year-old is told, or how close
 * they have to stand, should be a one-file change that a test can audit.
 *
 * Three groups, deliberately separated:
 *
 *  - {@link READINESS_MESSAGES} and {@link READINESS_CHECK_LABELS} are **verbatim from GDS §12**.
 *    `camera-readiness.test.ts` re-reads the design spec and fails if a word changes.
 *  - {@link INVENTED_READINESS_COPY} is everything the design spec is silent about. It is declared
 *    here, flagged in the Slice A report, and belongs in Jon's copy table before it is treated as
 *    approved (the D-049(5) pattern).
 *  - {@link UNSHIPPED_READINESS_MESSAGES} records the GDS §12 message this phase deliberately does
 *    not use, so its absence reads as a decision rather than an oversight.
 */

/**
 * GDS §12 "Example messages", verbatim. These are the child-facing headline states.
 *
 * `Compact Mode Ready` and `Full-Motion Mode Ready` are also the readiness half of the D-059
 * Compact/Full-Motion ruling: readiness *recommends* a mode from the measured distance, and the
 * parent chooses (Slice F).
 */
export const READINESS_MESSAGES = {
  fullMotionReady: 'Full-Motion Mode Ready',
  compactReady: 'Compact Mode Ready',
  moveBack: 'Move Back Slightly',
  moreLight: 'More Light Needed',
  cameraTooLow: 'Camera Too Low',
} as const;

/**
 * GDS §12's own "Check:" list, verbatim, used as the checklist row labels.
 *
 * Cooperative frame width is the one row of that list this record leaves out, and it still is:
 * it is inherently two-player, it ships in Phase 4 (Slice A′), and it lives in
 * {@link COOPERATIVE_READINESS_CHECK_LABEL} rather than here so that this record stays exactly the
 * set a single-player family is shown (D-061(2), sunset honoured rather than widened).
 *
 * **Flagged for the copy table:** these are the design spec's words, not a child's — "Pose system
 * loaded" is the kind of phrase GDS §12 itself warns against putting in front of a child. They are
 * used unchanged rather than rewritten so that nothing here invents language on the doc's behalf;
 * the child-facing sentence on the screen is the headline message above.
 */
export const READINESS_CHECK_LABELS = {
  browserSupport: 'Browser support',
  cameraAvailable: 'Camera available',
  permissionGranted: 'Permission granted',
  poseSystemLoaded: 'Pose system loaded',
  lighting: 'Lighting',
  playerDistance: 'Player distance',
  bodyVisibility: 'Body visibility',
  performance: 'Performance',
} as const;

/**
 * GDS §12 messages nothing ships, and why.
 *
 * **Empty since Slice A′.** Its one member — "Cooperative Mode Needs More Width" — was held back by
 * D-061(2) with an explicit sunset ("belongs to Phase 4, CAM-07"), and Phase 4 is where it ships:
 * see {@link COOPERATIVE_READINESS_MESSAGE}. The list stays, rather than being deleted, because the
 * next document silence deserves the same visible home.
 */
export const UNSHIPPED_READINESS_MESSAGES: readonly string[] = [];

/**
 * GDS §12's ninth check, verbatim — the row a two-hero sitting gets and a single-player one does
 * not (D-061(2)'s sunset, CAM-07).
 *
 * Kept out of {@link READINESS_CHECK_LABELS} on purpose: that record is the single-player checklist,
 * and a Phase 3 test reads it to prove a solo family is never shown cooperative wording. Two records
 * keep that proof standing while this one ships.
 */
export const COOPERATIVE_READINESS_CHECK_LABEL = 'Cooperative frame width';

/** GDS §12's cooperative example message, verbatim. */
export const COOPERATIVE_READINESS_MESSAGE = 'Cooperative Mode Needs More Width';

/**
 * Everything a two-hero readiness screen says that no document contains. **All invented, all
 * flagged for Jon's copy table** (the D-049(5)/D-061(4) pattern).
 *
 * The first line is child-facing and is written like the rest of the checklist: it describes the
 * picture, never a child — a camera that has found one of two bodies is a framing fact. The rest is
 * grown-up-facing, in the register the setup screen and the stage notices already use, because what
 * a play space can and cannot fit is a decision a grown-up makes.
 */
export const INVENTED_COOPERATIVE_READINESS_COPY = {
  /** Only one body in the picture. GDS §12 has no message for it. */
  bothHeroesNeeded: 'Both Heroes In The Picture',
  /** Why the Compact option is missing from a two-hero sitting (D-076(4)). */
  fullMotionOnly:
    'Two heroes need the Full-Motion play space — about 1.5 to 2.5 metres away, with room for a lane each. Compact is for one player in a small space.',
  /** What a family whose room cannot fit two lanes does instead (D-076(4)'s ruled fallback). */
  keyboardInstead:
    'If the room cannot fit two lanes, both heroes can still play the whole mission with the keyboard.',
  /**
   * The two-hero camera state, said conditionally and truthfully (Slice D′, retiring D-078(2)).
   *
   * Slice A′ said "two-hero camera play is not ready yet" and Slice C′ added "the mission itself
   * still runs on the keyboard". Both were true when they were written; from Slice D′ neither is,
   * so they are **gone** rather than softened. What replaces them is conditional on the one fact
   * that decides it — whether both heroes have been to hero training in this sitting — and each
   * branch is true on its own terms.
   */
  trainBothTitle: 'Two-hero camera play needs both heroes trained',
  trainBothBody:
    'Hero 1 and Hero 2 each do their own hero training, so the camera learns both of them. Until they have, the mission runs on the keyboard — both heroes have their own keys and the whole mission works.',
  cameraReadyTitle: 'Both heroes are ready for camera play',
  cameraReadyBody:
    'Hero 1 and Hero 2 have both done their hero training, so this mission can be played with the camera. The keyboard keeps working the whole time.',
  /**
   * One hero trained and one not — a real, playable sitting since Slice F′ (D-076(5)).
   *
   * Slice D′'s two branches were true only because the entry guard was all-or-nothing: a sitting
   * with one trained hero stayed wholly on the keyboard, so "needs both heroes trained" described
   * it correctly. The guard is per child now, so that sentence would be false here, and a third
   * branch says what is actually the case.
   *
   * It names no hero and compares nobody: it describes how the room is wired and what would change
   * it. "Both heroes play the whole mission" is the load-bearing half — a grown-up must not read
   * this as one child getting a lesser game.
   */
  oneHeroTitle: 'One hero is ready for camera play',
  oneHeroBody:
    'One hero has done their hero training, so the camera can read them. The other hero plays with their own keys, and both heroes play the whole mission either way. Run hero training for the other hero too and the camera will read both of them.',
  /** Starts the sitting the grown-up just set up, from this screen. */
  startMission: 'Start the mission',
  /** The same start, for a sitting that can genuinely be played with the camera. */
  startMissionWithCamera: 'Start the mission with the camera',
} as const;

/**
 * Copy no document contains. **Every string here is invented and awaiting approval.**
 *
 * Kept plain, kind and free of jargon (GDS §12), non-competitive, and careful never to describe a
 * child as having done something wrong — a camera that cannot see somebody is a hardware fact.
 */
export const INVENTED_READINESS_COPY = {
  /** Screen title. */
  pageTitle: 'Camera check',
  /** One line under the title, explaining what this screen is for. */
  pageLede: 'Let us make sure the camera can see you before the mission starts.',
  /** Shown while a check has not finished yet. */
  checking: 'Getting ready…',
  /** Nobody is in frame at all — GDS §12 has no message for this. */
  stepIntoView: 'Step Into The Picture',
  /** The missing counterpart to "Move Back Slightly". */
  comeCloser: 'Come A Little Closer',
  /** The machine is keeping up, but only just (CAM-06's low-performance path). */
  workingHard: 'Camera Is Working Hard',
  /** Camera refused. Child-facing line first; the instruction is for the grown-up beside them. */
  permissionDeniedTitle: 'The camera is having a rest',
  permissionDeniedBody:
    'Thrive Games needs a grown-up to say yes to the camera. Ask them to choose Allow for the camera, then press Try again. You can still play the whole mission with the keyboard.',
  /** Retry after a refusal. */
  retry: 'Try again',
  /** Leaves the readiness screen. */
  back: 'Back',
  /** The browser cannot do camera capture at all. */
  unsupportedBrowserTitle: 'This browser cannot do camera play',
  unsupportedBrowserBody:
    'The camera game needs a different browser. You can still play the whole mission with the keyboard.',
  /** The pose model did not load. Nothing the child did; offer the retry and the keyboard. */
  poseUnavailableTitle: 'Hero vision needs another go',
  poseUnavailableBody:
    'Something did not load. Press Try again, or play the whole mission with the keyboard.',
  /** No camera hardware at all. */
  noCameraTitle: 'No camera found',
  noCameraBody:
    'This computer cannot find a camera. You can still play the whole mission with the keyboard.',
  /** Link from Home (architecture §2 lists a camera readiness shortcut there). */
  homeShortcut: 'Camera check',
  /** The persistent camera-active indicator (architecture §23: "Clear camera indicator"). */
  cameraActive: 'Camera on',
} as const;

/**
 * The numbers the readiness checks decide with. **All invented** (recon PS3-3/PS3-6, D-059), seeded
 * from the Slice 0 spike and from GDS §6's distance bands, and due for tuning with a real child in
 * front of a real camera at Phase 6.
 *
 * Distance is read as apparent body height: the vertical span of the visible core landmarks as a
 * fraction of the frame. It is a proxy for metres, and deliberately so — a camera that reports no
 * intrinsics cannot give metres, and a child at the same distance in front of a wide-angle laptop
 * camera and a Logitech looks different in each. GDS §6's Compact ~1–1.5 m sits at the larger
 * apparent height, Full-Motion ~1.5–2.5 m at the smaller.
 */
export const CAMERA_READINESS_THRESHOLDS = {
  lighting: {
    /** Mean frame luminance, 0–1. Below this the pose model starts losing the body entirely. */
    minMeanLuminance: 0.18,
  },
  distance: {
    /** Body fills this much of the frame or more: too close, nothing below the knees is usable. */
    tooCloseBodyHeight: 0.9,
    /** At or above this (and below too-close) the child is in the Compact band. */
    compactMinBodyHeight: 0.55,
    /** At or above this (and below Compact) the child is in the Full-Motion band. */
    fullMotionMinBodyHeight: 0.3,
    /** Below this the child is too far for either mode. */
    tooFarBodyHeight: 0.18,
  },
  /**
   * How often the readiness screen re-measures, in ms. **Invented.** A checklist is not gameplay:
   * four readings a second is faster than a grown-up can read a row and far cheaper than the
   * `poseTargetFps` cadence Slice B's gameplay loop will use.
   */
  sampleIntervalMs: 250,
  /**
   * The two-hero numbers (GDS §12 "Cooperative frame width"). **Both invented, both flagged**, in
   * the D-061(3) family: GDS §12 names the check and its message and gives no threshold at all, and
   * MIN §6 gives only Full-Motion's ~1.5–2.5 m band in metres, which a camera without intrinsics
   * cannot measure. They guide where two children stand; they score nothing.
   */
  cooperative: {
    /**
     * How far apart the two bodies' centres must be, as a fraction of frame width.
     *
     * Seeded from what the two lanes have to be for the zone detectors to tell them apart at all:
     * two children sharing one half of the picture are not two lanes, and the Slice 0′ spike found
     * that a body half out of frame can produce two readings only a few percent apart
     * (D-077(2)(b)). Tuned at the batched physical session with two children in the room.
     */
    minCentreSeparation: 0.22,
    /**
     * Consecutive frames a changed two-hero verdict must hold before the screen says it.
     *
     * D-077(2)(d) measured second-body acquisition at **5–7 frames**; a check that needs both
     * bodies at once would otherwise flip between "needs more width" and "ready" while a family
     * stands perfectly still. Three is the smallest window that absorbs a dropped detection either
     * side of the threshold without making a family wait to be told something true.
     */
    confirmFrames: 3,
  },
  visibility: {
    /** Fraction of the core landmarks that must be confidently visible. */
    minVisibleCoreFraction: 0.7,
    /**
     * How much clear frame there must be above the head. Less than this, with the body reaching
     * the top edge, is what "Camera Too Low" means: the camera is aimed at the floor and the
     * child's head is being cut off.
     */
    headroomFraction: 0.04,
  },
} as const;

export type CameraReadinessThresholds = typeof CAMERA_READINESS_THRESHOLDS;
